import { useCallback, useEffect, useState } from 'react'
import api from '../api/axios'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

// Crash-safe: on iOS Safari (non-PWA) the `Notification` global may not exist,
// and a bare `Notification` reference throws ReferenceError (optional chaining
// does NOT protect identifier resolution). Always guard with typeof + try.
function getPermission() {
  try {
    return (typeof Notification !== 'undefined' && Notification.permission) || 'default'
  } catch {
    return 'default'
  }
}

function isIOS() {
  try { return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '') } catch { return false }
}

function isStandalone() {
  try {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || window.navigator.standalone === true // iOS home-screen PWA
  } catch { return false }
}

export function usePushNotifications() {
  const supported =
    typeof window !== 'undefined' &&
    typeof Notification !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window

  const [permission, setPermission] = useState(getPermission)
  const [subscribed, setSubscribed] = useState(false)
  const [loading, setLoading]       = useState(false)
  const [vapidKey, setVapidKey]     = useState(null)
  const [reason, setReason]         = useState('') // human-readable explanation when unavailable/failed

  // Fetch VAPID public key
  useEffect(() => {
    if (!supported) return
    api.get('/push/vapid-public-key')
      .then(r => {
        const key = r.data?.publicKey
        if (key) setVapidKey(key)
        else setReason('Server tərəfdə bildiriş açarı (VAPID) konfiqurasiya edilməyib.')
      })
      .catch(() => setReason('Bildiriş xidməti hazırda əlçatan deyil.'))
  }, [supported])

  // Restore subscribed flag
  useEffect(() => {
    if (!supported) return
    try { setSubscribed(!!localStorage.getItem('push_subscribed')) } catch {}
  }, [supported])

  const requestAndSubscribe = useCallback(async () => {
    setReason('')
    if (!supported) {
      if (isIOS() && !isStandalone()) {
        setReason('iOS-da bildirişlər üçün əvvəlcə Safari → Paylaş → "Ana ekrana əlavə et" ilə tətbiq kimi qurun, sonra tətbiqi açıb aktivləşdirin (iOS 16.4+).')
      } else {
        setReason('Bu brauzer push bildirişləri dəstəkləmir.')
      }
      return false
    }
    if (!vapidKey) {
      setReason('Server tərəfdə bildiriş açarı (VAPID) konfiqurasiya edilməyib.')
      return false
    }
    setLoading(true)
    try {
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') {
        setReason(perm === 'denied'
          ? 'Bildiriş icazəsi bloklanıb. Brauzer/sayt ayarlarından icazə verin.'
          : 'Bildiriş icazəsi verilmədi.')
        return false
      }

      const reg = await navigator.serviceWorker.ready
      const existing = await reg.pushManager.getSubscription()
      if (existing) { await existing.unsubscribe() }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      const keys = subscription.toJSON().keys || {}
      await api.post('/push/subscribe', {
        endpoint: subscription.endpoint,
        p256dhKey: keys.p256dh || '',
        authKey: keys.auth || '',
      })
      setSubscribed(true)
      try { localStorage.setItem('push_subscribed', '1') } catch {}
      return true
    } catch (err) {
      setReason('Bildiriş aktivləşdirilə bilmədi: ' + (err?.message || 'naməlum xəta'))
      return false
    } finally {
      setLoading(false)
    }
  }, [supported, vapidKey])

  const unsubscribe = useCallback(async () => {
    setReason('')
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`)
        await sub.unsubscribe()
      }
      setSubscribed(false)
      try { localStorage.removeItem('push_subscribed') } catch {}
    } catch {}
  }, [])

  return {
    supported,
    permission,
    subscribed,
    loading,
    vapidKey,
    reason,
    iosNeedsInstall: isIOS() && !isStandalone(),
    requestAndSubscribe,
    unsubscribe,
  }
}
